/**
 * 🎯 permissionHelpers - 권한 체크 함수 표준화 유틸리티
 *
 * 모든 권한 체크 로직을 표준화된 방식으로 제공
 * - 도메인별 권한 체크
 * - 필드별 권한 체크
 * - 액션별 권한 체크
 */

import { BusinessDomain } from '@shared/types';
import { InternalEntityPermission } from '@shared/types/permissions';
import { FieldPermission } from './types/FieldPermissions';
import {
  createPermissionString,
  convertLegacyPermissionToStandard,
} from '@shared/types/business-domain';
import type { TableUserPermissionContext } from '../components/common/DynamicTable/types';

/**
 * 권한 컨텍스트 인터페이스
 */
export interface PermissionContext {
  domainPermissions: Array<{
    domain: BusinessDomain;
    level: 'read-only' | 'read-write';
    permissions: string[];
  }>;
  globalPermissions: string[];
}

/** TableUserPermissionContext → PermissionContext (DynamicTable / workspace adapters) */
export function toPermissionContext(
  userPermissionContext: TableUserPermissionContext
): PermissionContext {
  return {
    domainPermissions: userPermissionContext.domainPermissions.map((dp) => ({
      domain: dp.domain,
      level: dp.level === 'admin' ? 'read-write' : (dp.level as 'read-only' | 'read-write'),
      permissions: dp.permissions.map((p) => String(p)),
    })),
    globalPermissions: userPermissionContext.globalPermissions.map((p) => String(p)),
  };
}

/**
 * 필드 권한 컨텍스트 인터페이스
 */
export interface FieldPermissionContext {
  permissions: FieldPermission[];
}

/**
 * 특정 도메인에 대한 CRUD 권한 체크
 *
 * @param permissionContext 권한 컨텍스트
 * @param domain 비즈니스 도메인
 * @param action 액션 (create, read, update, delete)
 * @returns 권한 여부
 */
export function hasDomainPermission(
  permissionContext: PermissionContext,
  domain: BusinessDomain,
  action: 'create' | 'read' | 'update' | 'delete'
): boolean {
  // ✅ 표준 형식으로 권한 문자열 생성
  const standardPermission = createPermissionString(domain, action);

  // 전역 권한 체크 (표준 형식 또는 레거시 BigInteger 형식 지원)
  const globalPerms = permissionContext.globalPermissions.map((p) => {
    const converted = convertLegacyPermissionToStandard(p);
    return converted || p;
  });
  if (globalPerms.includes(standardPermission)) {
    return true;
  }

  // 도메인별 권한 체크
  const domainPermission = permissionContext.domainPermissions.find((dp) => dp.domain === domain);

  if (!domainPermission) {
    return false;
  }

  // 표준 형식 또는 레거시 형식 지원
  const domainPerms = domainPermission.permissions.map((p) => {
    const converted = convertLegacyPermissionToStandard(p);
    return converted || p;
  });

  return domainPerms.includes(standardPermission);
}

/**
 * 액션별 권한 체크 (basePermission:action 형식)
 *
 * @param permissionContext 권한 컨텍스트
 * @param basePermission 기본 권한 (예: 'internal_entity')
 * @param action 액션 (예: 'create')
 * @returns 권한 여부
 */
export function hasActionPermission(
  permissionContext: PermissionContext,
  basePermission: string,
  action: string
): boolean {
  // 먼저 기본 권한 체크 (예: internal_entity:read)
  const domainPermission = basePermission as InternalEntityPermission;
  if (hasPermission(permissionContext, domainPermission)) {
    return true;
  }

  // 세부 액션 권한 체크 (예: internal_entity:create)
  const fullPermission = `${basePermission}:${action}` as InternalEntityPermission;
  return hasPermission(permissionContext, fullPermission);
}

/**
 * 내부 권한 체크 (InternalEntityPermission 타입 직접 체크)
 *
 * @param permissionContext 권한 컨텍스트
 * @param permission 체크할 권한
 * @returns 권한 여부
 */
export function hasPermission(
  permissionContext: PermissionContext,
  permission: InternalEntityPermission
): boolean {
  // 전역 권한 체크
  const globalPerms = permissionContext.globalPermissions.map((p) => {
    const converted = convertLegacyPermissionToStandard(p);
    return converted || p;
  });
  if (globalPerms.includes(permission as string)) {
    return true;
  }

  // 도메인별 권한 체크
  for (const domainPerm of permissionContext.domainPermissions) {
    const domainPerms = domainPerm.permissions.map((p) => {
      const converted = convertLegacyPermissionToStandard(p);
      return converted || p;
    });
    if (domainPerms.includes(permission as string)) {
      return true;
    }
  }

  return false;
}

/**
 * 필드 권한 체크
 *
 * @param fieldPermissionContext 필드 권한 컨텍스트
 * @param permission 체크할 필드 권한
 * @returns 권한 여부
 */
export function hasFieldPermission(
  fieldPermissionContext: FieldPermissionContext,
  permission: FieldPermission
): boolean {
  return fieldPermissionContext.permissions.includes(permission);
}

/**
 * 여러 권한 중 하나라도 있는지 체크 (OR)
 *
 * @param permissionContext 권한 컨텍스트
 * @param permissions 체크할 권한 배열
 * @returns 권한 여부
 */
export function hasAnyPermission(
  permissionContext: PermissionContext,
  permissions: InternalEntityPermission[]
): boolean {
  return permissions.some((permission) => hasPermission(permissionContext, permission));
}

/**
 * 모든 권한이 있는지 체크 (AND)
 *
 * @param permissionContext 권한 컨텍스트
 * @param permissions 체크할 권한 배열
 * @returns 권한 여부
 */
export function hasAllPermissions(
  permissionContext: PermissionContext,
  permissions: InternalEntityPermission[]
): boolean {
  return permissions.every((permission) => hasPermission(permissionContext, permission));
}

/**
 * 권한 헬퍼 전체 export
 */
export default {
  hasDomainPermission,
  hasActionPermission,
  hasPermission,
  hasFieldPermission,
  hasAnyPermission,
  hasAllPermissions,
};
